import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { updateVideoMetadata } from '../services/api';

const EditVideoModal = ({ show, onHide, video, onSaveSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    cost_thb: '',
    cost_thb_waan: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when modal opens with new video data
  useEffect(() => {
    if (video && show) {
      setFormData({
        name: video.name || '',
        cost_thb: video.cost_thb?.toString() || '',
        cost_thb_waan: video.cost_thb_waan?.toString() || ''
      });
      setError(null);
    }
  }, [video, show]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      // Build update payload - only include changed fields
      const updatePayload = {};
      
      if (formData.name && formData.name !== video.name) {
        updatePayload.name = formData.name;
      }
      
      const newCostThb = parseFloat(formData.cost_thb);
      if (!isNaN(newCostThb) && newCostThb !== video.cost_thb) {
        updatePayload.cost_thb = newCostThb;
      }
      
      const newCostThbWaan = parseFloat(formData.cost_thb_waan);
      if (!isNaN(newCostThbWaan) && newCostThbWaan !== video.cost_thb_waan) {
        updatePayload.cost_thb_waan = newCostThbWaan;
      }

      if (Object.keys(updatePayload).length === 0) {
        // No changes
        handleClose();
        return;
      }

      await updateVideoMetadata(video.id, updatePayload);
      
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      handleClose();
    } catch (err) {
      console.error('Failed to update video:', err);
      setError(`Failed to save: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setError(null);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop={isSaving ? 'static' : true}>
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>Edit Video</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              disabled={isSaving}
              placeholder="Video title"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Cost (Won) - THB</Form.Label>
            <Form.Control
              type="number"
              name="cost_thb"
              value={formData.cost_thb}
              onChange={handleInputChange}
              disabled={isSaving}
              step="0.01"
              min="0"
              placeholder="Cost for Won"
            />
            <Form.Text className="text-muted">
              Original auto-calculated: {video?.length_seconds ? (video.length_seconds / 60 * 3).toFixed(2) : '0'} THB
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Cost (Waan) - THB</Form.Label>
            <Form.Control
              type="number"
              name="cost_thb_waan"
              value={formData.cost_thb_waan}
              onChange={handleInputChange}
              disabled={isSaving}
              step="0.01"
              min="0"
              placeholder="Cost for Waan"
            />
            <Form.Text className="text-muted">
              Original auto-calculated: {video?.length_seconds ? (video.length_seconds / 60 * 2).toFixed(2) : '0'} THB
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditVideoModal;
